import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const COLORS = {
  sensory: '#2493A2',
  integration: '#57BDA2',
  symbolic: '#DCB13C',
  capstone: '#E85D75',
};

const NODE_LABELS = ['Experience', 'Interpretation', 'Identity Claim', 'Attention Bias'];
const NODE_COLORS = [COLORS.sensory, COLORS.integration, COLORS.symbolic, COLORS.capstone];

export function ReinforcementLoop() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svgRef.current) return;

    svg.selectAll('*').remove();

    const W = 280;
    const H = 280;
    const cx = W / 2;
    const cy = H / 2;
    const outerR = 95;
    const innerR = 60; // tightened radius

    // Arrowhead marker
    const defs = svg.append('defs');

    NODE_COLORS.forEach((color, i) => {
      defs.append('marker')
        .attr('id', `arrow-loop-${i}`)
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 8)
        .attr('refY', 5)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,2 L8,5 L0,8 Z')
        .attr('fill', color);
    });

    // Compute node positions on a circle
    function nodePositions(radius: number) {
      return NODE_LABELS.map((_, i) => {
        const angle = (i / NODE_LABELS.length) * 2 * Math.PI - Math.PI / 2;
        return {
          x: cx + radius * Math.cos(angle),
          y: cy + radius * Math.sin(angle),
        };
      });
    }

    const outerPositions = nodePositions(outerR);

    // -- Draw curved arrows between nodes --
    const arrowGroup = svg.append('g');

    function drawArrows(positions: { x: number; y: number }[]) {
      arrowGroup.selectAll('*').remove();
      positions.forEach((from, i) => {
        const to = positions[(i + 1) % positions.length];
        const midAngle = ((i + 0.5) / NODE_LABELS.length) * 2 * Math.PI - Math.PI / 2;
        const bulge = 20;
        const midPoint = {
          x: (from.x + to.x) / 2 + bulge * Math.cos(midAngle),
          y: (from.y + to.y) / 2 + bulge * Math.sin(midAngle),
        };

        arrowGroup.append('path')
          .attr('d', `M${from.x},${from.y} Q${midPoint.x},${midPoint.y} ${to.x},${to.y}`)
          .attr('fill', 'none')
          .attr('stroke', NODE_COLORS[i])
          .attr('stroke-width', 2)
          .attr('stroke-opacity', 0.6)
          .attr('marker-end', `url(#arrow-loop-${i})`);
      });
    }

    drawArrows(outerPositions);

    // -- Draw nodes --
    const nodeGroup = svg.append('g');

    function drawNodes(positions: { x: number; y: number }[]) {
      nodeGroup.selectAll('*').remove();
      positions.forEach((pos, i) => {
        const g = nodeGroup.append('g')
          .attr('transform', `translate(${pos.x}, ${pos.y})`);

        g.append('circle')
          .attr('r', 22)
          .attr('fill', NODE_COLORS[i])
          .attr('opacity', 0.15);

        g.append('circle')
          .attr('r', 22)
          .attr('fill', 'none')
          .attr('stroke', NODE_COLORS[i])
          .attr('stroke-width', 1.5);

        // Multi-line text for longer labels
        const words = NODE_LABELS[i].split(' ');
        if (words.length === 1) {
          g.append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .attr('fill', 'var(--color-text)')
            .attr('font-size', '8px')
            .attr('font-weight', '600')
            .text(NODE_LABELS[i]);
        } else {
          words.forEach((word, wi) => {
            g.append('text')
              .attr('text-anchor', 'middle')
              .attr('dy', `${(wi - (words.length - 1) / 2) * 1.1 + 0.35}em`)
              .attr('fill', 'var(--color-text)')
              .attr('font-size', '8px')
              .attr('font-weight', '600')
              .text(word);
          });
        }
      });
    }

    drawNodes(outerPositions);

    // -- Pulse particle that travels the loop --
    const particleGroup = svg.append('g');
    let cancelled = false;

    function sendPulse(positions: { x: number; y: number }[], step: number) {
      if (cancelled) return;
      const from = positions[step % positions.length];
      const to = positions[(step + 1) % positions.length];

      const pulse = particleGroup.append('circle')
        .attr('cx', from.x)
        .attr('cy', from.y)
        .attr('r', 4)
        .attr('fill', NODE_COLORS[step % NODE_COLORS.length])
        .attr('opacity', 0.9);

      pulse.transition()
        .duration(500)
        .ease(d3.easeLinear)
        .attr('cx', to.x)
        .attr('cy', to.y)
        .attr('opacity', 0)
        .remove()
        .on('end', () => {
          if (!cancelled) sendPulse(positions, step + 1);
        });
    }

    // -- Tightening animation cycle --
    function runCycle() {
      if (cancelled) return;

      const outerPos = nodePositions(outerR);
      drawArrows(outerPos);
      drawNodes(outerPos);

      sendPulse(outerPos, 0);

      // After a few pulses, tighten the circle
      setTimeout(() => {
        if (cancelled) return;

        const steps = 20;
        let step = 0;

        function tightenStep() {
          if (cancelled || step > steps) {
            // Hold tight then expand back
            setTimeout(() => {
              if (cancelled) return;
              // Expand back
              let expandStep = 0;
              function expandBack() {
                if (cancelled || expandStep > steps) {
                  setTimeout(runCycle, 800);
                  return;
                }
                const t = expandStep / steps;
                const r = innerR + (outerR - innerR) * t;
                const pos = nodePositions(r);
                drawArrows(pos);
                drawNodes(pos);
                expandStep++;
                setTimeout(expandBack, 30);
              }
              expandBack();
            }, 2000);
            return;
          }

          const t = step / steps;
          const r = outerR - (outerR - innerR) * d3.easeCubicInOut(t);
          const pos = nodePositions(r);
          drawArrows(pos);
          drawNodes(pos);
          step++;
          setTimeout(tightenStep, 40);
        }

        tightenStep();
      }, 3000);
    }

    const startTimeout = setTimeout(runCycle, 300);

    return () => {
      cancelled = true;
      clearTimeout(startTimeout);
      svg.selectAll('*').interrupt();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-rotate" /> Reinforcement Loop
      </h4>
      <svg ref={svgRef} viewBox="0 0 280 280" />
      <p className="viz-container__caption">Identity reinforces itself.</p>
    </div>
  );
}
